import React, { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import styled from 'styled-components';
import * as api from '../services/api';

interface MFASetupModalProps {
  show: boolean;
  onHide: () => void;
  onComplete: () => void;
}

const Overlay = styled.div<{ $show: boolean }>`
  display: ${(p) => (p.$show ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  z-index: 1000;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 560px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0 4px;
  &:hover {
    color: #111;
  }
`;

const Body = styled.div`
  padding: 24px;
  overflow-y: auto;
`;

const Alert = styled.div<{ $variant?: 'info' | 'success' | 'warning' | 'danger' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  background: ${(p) =>
    p.$variant === 'danger' ? '#fef2f2' : p.$variant === 'success' ? '#f0fdf4' : p.$variant === 'warning' ? '#fffbeb' : '#eff6ff'};
  color: ${(p) => (p.$variant === 'danger' ? '#991b1b' : p.$variant === 'success' ? '#166534' : p.$variant === 'warning' ? '#92400e' : '#1e40af')};
  font-size: 0.9rem;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
  margin-bottom: 16px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button<{ $secondary?: boolean }>`
  padding: 10px 20px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  border: none;
  background: ${(p) => (p.$secondary ? '#6b7280' : '#2563eb')};
  color: white;
  &:hover:not(:disabled) {
    background: ${(p) => (p.$secondary ? '#4b5563' : '#1d4ed8')};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CodeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin: 16px 0;
  font-family: monospace;
  font-size: 14px;
`;

const CodeCell = styled.div`
  padding: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  text-align: center;
`;

const validationSchema = yup.object({
  code: yup
    .string()
    .required('Verification code is required')
    .length(6, 'Code must be 6 digits')
    .matches(/^\d+$/, 'Code must contain only numbers'),
});

const MFASetupModal: React.FC<MFASetupModalProps> = ({ show, onHide, onComplete }) => {
  const [step, setStep] = useState<'setup' | 'verify'>('setup');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  /** When step is 'verify', backup codes from API (single state update with setStep so they stay in sync). */
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerateQR = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await api.mfaSetup();
      if (response?.success && response?.data) {
        setQrCode(response.data.qr_code || '');
        setSecret(response.data.secret || '');
      } else {
        setErrorMessage((response as any)?.message || 'Failed to generate QR code');
      }
    } catch (err: any) {
      setErrorMessage(err?.data?.message || err?.message || 'Failed to generate QR code.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && step === 'setup') {
      handleGenerateQR();
    }
  }, [show, step]);

  const handleVerifyCode = async (code: string) => {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await api.mfaVerifySetup(code);
      if (response.success) {
        setBackupCodes(response.backupCodes ?? []);
        setStep('verify');
      } else {
        setErrorMessage(response.message || 'Invalid verification code');
      }
    } catch (err: any) {
      setErrorMessage(err?.data?.message || err?.message || 'Invalid verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('setup');
    setQrCode('');
    setSecret('');
    setBackupCodes([]);
    setErrorMessage('');
    onHide();
  };

  const handleComplete = () => {
    setStep('setup');
    setQrCode('');
    setSecret('');
    setBackupCodes([]);
    setErrorMessage('');
    onComplete();
  };

  if (!show) return null;

  return (
    <Overlay $show={show} onClick={handleClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Set up Multi-Factor Authentication (MFA)</Title>
          <CloseBtn type="button" onClick={handleClose} aria-label="Close">
            &times;
          </CloseBtn>
        </Header>
        <Body>
          {step === 'setup' ? (
            <>
              <Alert $variant="info">
                <strong>MFA is required.</strong> Use Google Authenticator, Microsoft Authenticator, or any TOTP-compatible app.
              </Alert>
              {loading && !qrCode && <p>Generating QR code...</p>}
              {qrCode && (
                <>
                  <p style={{ marginBottom: 12 }}>
                    <strong>Step 1:</strong> Scan this QR code with your authenticator app.
                  </p>
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <img
                      src={qrCode}
                      alt="MFA QR Code"
                      style={{ maxWidth: 240, border: '2px solid #e5e7eb', borderRadius: 8, padding: 8, background: 'white' }}
                    />
                  </div>
                  {secret && (
                    <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
                      Can't scan? Enter manually: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{secret}</code>
                    </p>
                  )}
                  <p style={{ marginBottom: 8 }}>
                    <strong>Step 2:</strong> Enter the 6-digit code from your app:
                  </p>
                  <Formik
                    initialValues={{ code: '' }}
                    validationSchema={validationSchema}
                    onSubmit={({ code }) => handleVerifyCode(code)}
                  >
                    {({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
                      <form onSubmit={handleSubmit}>
                        {errorMessage && (
                          <Alert $variant="danger" style={{ marginBottom: 12 }}>
                            {errorMessage}
                          </Alert>
                        )}
                        <Label>Verification code</Label>
                        <Input
                          type="text"
                          name="code"
                          value={values.code ?? ''}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setFieldValue('code', v);
                          }}
                          placeholder="000000"
                          autoComplete="one-time-code"
                        />
                        {touched.code && errors.code && <Alert $variant="danger">{errors.code}</Alert>}
                        <ButtonRow>
                          <Button type="button" $secondary onClick={handleClose}>
                            Cancel
                          </Button>
                          <Button type="submit" disabled={loading}>
                            {loading ? 'Verifying...' : 'Verify & complete setup'}
                          </Button>
                        </ButtonRow>
                      </form>
                    )}
                  </Formik>
                </>
              )}
            </>
          ) : (
            <>
              <Alert $variant="success">MFA setup complete.</Alert>
              <Alert $variant="warning">
                Save your backup codes in a safe place. They will not be shown again.
              </Alert>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>Your backup codes:</p>
              <CodeGrid>
                {backupCodes.length > 0 ? (
                  backupCodes.map((code, i) => <CodeCell key={i}>{code}</CodeCell>)
                ) : (
                  <CodeCell style={{ gridColumn: '1 / -1', color: '#6b7280' }}>
                    Backup codes could not be loaded. Save your secret key instead — you can use it in your authenticator app to recover access:{' '}
                    {secret ? <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{secret}</code> : ' (already hidden)'}
                  </CodeCell>
                )}
              </CodeGrid>
              <ButtonRow>
                <Button type="button" onClick={handleComplete}>
                  I've saved my backup codes
                </Button>
              </ButtonRow>
            </>
          )}
        </Body>
      </ModalBox>
    </Overlay>
  );
};

export default MFASetupModal;
